import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Loader2, ArrowRight, ChevronDown } from 'lucide-react';
import { fiatService } from '../../../../services/api';
import { useIntakeStore } from '../../../../stores/useIntakeStore';
import { toErrorMessage } from '../../../../utils/safeStr';
import { MATCH, STYLES } from '../copy';
import type { OffRampToolPanelProps } from '../types';
import { TierBadge } from '../shared/TierBadge';
import { ResultEmptyHint } from '../shared/ResultEmptyHint';

export function OffRampMatchPanel({ className, onComplete }: OffRampToolPanelProps) {
    const [searchParams] = useSearchParams();
    const queryVpa = searchParams.get('vpa') || searchParams.get('upi') || '';
    const queryAccount = searchParams.get('account') || '';
    const queryName = searchParams.get('name') || '';

    const [vpa, setVpa] = useState(() => queryVpa);
    const [account, setAccount] = useState(() => queryAccount);
    const [ifsc, setIfsc] = useState('');
    const [name, setName] = useState(() => queryName);
    const [narration, setNarration] = useState('');
    const [upiUri, setUpiUri] = useState('');
    const [showMore, setShowMore] = useState(() => Boolean(queryAccount));
    const [matchLoading, setMatchLoading] = useState(false);
    const [resolveLoading, setResolveLoading] = useState(false);
    const [matchResult, setMatchResult] = useState<any>(null);
    const [resolveResult, setResolveResult] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    // Auto-match when navigating with query parameters (e.g. from P2P Intelligence bridge)
    useEffect(() => {
        if (queryVpa || queryAccount) {
            const timer = setTimeout(() => {
                const el = document.getElementById('offramp-match-panel');
                el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 150);

            const autoMatch = async () => {
                setMatchLoading(true);
                setError(null);
                setMatchResult(null);
                try {
                    const caseId = useIntakeStore.getState().caseId;
                    const res = await fiatService.counterpartyMatch(
                        {
                            vpa: queryVpa || undefined,
                            account: queryAccount || undefined,
                            name: queryName || undefined,
                        },
                        caseId,
                    );
                    setMatchResult(res);
                    onComplete?.();
                } catch (e) {
                    setError(toErrorMessage(e));
                } finally {
                    setMatchLoading(false);
                }
            };
            void autoMatch();
            return () => clearTimeout(timer);
        }
    }, [queryVpa, queryAccount, queryName, onComplete]);

    const runResolvePayee = async () => {
        if (!upiUri.trim() && !vpa && !(account && ifsc)) {
            setError('Paste a upi://pay?… URI, or enter VPA / account+IFSC.');
            return;
        }
        setResolveLoading(true);
        setError(null);
        setResolveResult(null);
        try {
            const res = await fiatService.resolvePayee({
                upi_uri: upiUri.trim() || undefined,
                vpa: vpa || undefined,
                account: account || undefined,
                ifsc: ifsc || undefined,
                name: name || undefined,
                enrich_registry: true,
            });
            setResolveResult(res);
            if (res?.vpa) setVpa(res.vpa);
            if (res?.holder_name) setName(res.holder_name);
            if (res?.match) setMatchResult(res.match);
            onComplete?.();
        } catch (e) {
            setError(toErrorMessage(e));
        } finally {
            setResolveLoading(false);
        }
    };

    const runMatch = async () => {
        if (!vpa && !account) {
            setError('Enter at least a VPA or an account number.');
            return;
        }
        setMatchLoading(true);
        setError(null);
        setMatchResult(null);
        try {
            const caseId = useIntakeStore.getState().caseId;
            const res = await fiatService.counterpartyMatch(
                {
                    vpa: vpa || undefined,
                    account: account || undefined,
                    ifsc: ifsc || undefined,
                    name: name || undefined,
                    narration: narration || undefined,
                },
                caseId,
            );
            setMatchResult(res);
            onComplete?.();
        } catch (e) {
            setError(toErrorMessage(e));
        } finally {
            setMatchLoading(false);
        }
    };

    return (
        <div id="offramp-match-panel" className={`${STYLES.card} ${className ?? ''}`}>
            <ResultEmptyHint>{MATCH.empty}</ResultEmptyHint>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                    <label className={STYLES.label}>UPI payment QR URI (optional)</label>
                    <input
                        className={STYLES.input}
                        placeholder="upi://pay?pa=seller@okhdfcbank&pn=SURESH%20B&cu=INR"
                        value={upiUri}
                        onChange={(e) => setUpiUri(e.target.value)}
                    />
                </div>
                <div>
                    <label className={STYLES.label}>Beneficiary VPA</label>
                    <input
                        className={STYLES.input}
                        placeholder="seller@okhdfcbank"
                        value={vpa}
                        onChange={(e) => setVpa(e.target.value)}
                    />
                </div>
                <div>
                    <label className={STYLES.label}>Beneficiary name (optional)</label>
                    <input
                        className={STYLES.input}
                        placeholder="e.g. WazirX / Zanmai Labs"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </div>
            </div>

            <button
                type="button"
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setShowMore((v) => !v)}
            >
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showMore ? 'rotate-180' : ''}`} />
                {MATCH.moreFields}
            </button>

            {showMore && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className={STYLES.label}>Account number</label>
                        <input
                            className={STYLES.input}
                            placeholder="50100xxxxxxx"
                            value={account}
                            onChange={(e) => setAccount(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className={STYLES.label}>IFSC</label>
                        <input
                            className={STYLES.input}
                            placeholder="HDFC0001234"
                            value={ifsc}
                            onChange={(e) => setIfsc(e.target.value)}
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <label className={STYLES.label}>Statement narration (optional)</label>
                        <input
                            className={STYLES.input}
                            placeholder="e.g. UPI-WAZIRX DEPOSIT-…"
                            value={narration}
                            onChange={(e) => setNarration(e.target.value)}
                        />
                    </div>
                </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
                <button
                    type="button"
                    className={STYLES.btn}
                    onClick={() => void runMatch()}
                    disabled={matchLoading || resolveLoading}
                >
                    {matchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    {MATCH.check}
                </button>
                <button
                    type="button"
                    className={STYLES.btnSecondary}
                    onClick={() => void runResolvePayee()}
                    disabled={resolveLoading || matchLoading}
                    title={MATCH.resolveHint}
                >
                    {resolveLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Search className="w-4 h-4" />
                    )}
                    {MATCH.resolve}
                </button>
                <span className="text-[11px] text-muted-foreground hidden sm:inline">{MATCH.resolveHint}</span>
                {error && <span className="text-xs text-red-500">{error}</span>}
            </div>

            {resolveResult && (
                <div className="border border-border rounded-md p-4 bg-secondary/30 space-y-2 text-xs">
                    <div className="flex items-center gap-2 flex-wrap">
                        <TierBadge determinism={resolveResult.determinism} />
                        <span className="font-semibold text-foreground">
                            {resolveResult.resolved
                                ? `Holder: ${resolveResult.holder_name}`
                                : MATCH.noHolder}
                        </span>
                        {resolveResult.source && (
                            <span className="text-[11px] text-muted-foreground">
                                via {resolveResult.source}
                            </span>
                        )}
                    </div>
                    {Array.isArray(resolveResult.steps) && (
                        <div className="text-[11px] text-muted-foreground space-y-0.5">
                            {resolveResult.steps.map((s: any) => (
                                <div key={s.step}>
                                    {s.step}: <span className="font-mono">{s.status}</span>
                                    {s.detail ? ` — ${s.detail}` : ''}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {matchResult && (
                <div className="border border-border rounded-md p-4 bg-secondary/30 space-y-2">
                    {matchResult.matched ? (
                        <>
                            <div className="flex items-center gap-2 flex-wrap">
                                <TierBadge determinism={matchResult.determinism} />
                                <span className="text-sm font-semibold text-foreground">
                                    {matchResult.display_name}
                                </span>
                                <span className="text-[11px] uppercase font-medium text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">
                                    {matchResult.node_type}
                                </span>
                                <span className="text-[11px] text-muted-foreground">
                                    match: {matchResult.match}
                                </span>
                            </div>
                            {matchResult.wallet_cluster_ref && (
                                <div className="text-xs text-foreground/80 flex items-center gap-1 flex-wrap">
                                    <ArrowRight className="w-3 h-3" /> Linked wallet:
                                    <code className="font-mono bg-secondary px-1 rounded">
                                        {matchResult.wallet_cluster_ref}
                                    </code>
                                    {matchResult.near_crypto_chain && (
                                        <span className="text-muted-foreground">
                                            ({matchResult.near_crypto_chain})
                                        </span>
                                    )}
                                    {(() => {
                                        const w = String(matchResult.wallet_cluster_ref);
                                        const chain =
                                            matchResult.near_crypto_chain ||
                                            (w.startsWith('T')
                                                ? 'TRX'
                                                : w.toLowerCase().startsWith('0x')
                                                  ? 'ETH'
                                                  : '');
                                        if (!w || w.includes('@')) return null;
                                        const qs = new URLSearchParams({ address: w });
                                        if (chain) qs.set('chain', chain);
                                        return (
                                            <a
                                                className="text-primary hover:underline ml-1"
                                                href={`/workbench?${qs.toString()}`}
                                            >
                                                {MATCH.openWorkbench}
                                            </a>
                                        );
                                    })()}
                                </div>
                            )}
                            {Array.isArray(matchResult.sources) && matchResult.sources.length > 0 && (
                                <div className="text-[11px] text-muted-foreground">
                                    Provenance: {matchResult.sources.join(', ')}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="flex items-start gap-2">
                            <TierBadge />
                            <div className="text-xs text-muted-foreground">
                                <p>{MATCH.boundaryPlain}</p>
                                {matchResult.note && <p className="mt-1">{matchResult.note}</p>}
                                {matchResult.normalised && (
                                    <div className="mt-1 font-mono text-[10px]">
                                        normalised: vpa={String(matchResult.normalised.vpa)} · account=
                                        {String(matchResult.normalised.account)}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
